import {
  DefaultApi as AppApi,
  Configuration as AppConfiguration
} from '@/types/app';
import {
  DefaultApi as CarePlanApi,
  Configuration as CarePlanConfiguration,
  ConfigurationParameters
} from '@/types/care-plan';
import {
  DefaultApi as CRMApi,
  Configuration as CRMConfiguration
} from '@/types/crm';
import {
  DefaultApi as CrmCampaignsApi,
  Configuration as CrmCampaignsConfiguration
} from '@/types/crm-campaigns';
import {
  DefaultApi as PhoneAgentApi,
  Configuration as PhoneAgentConfiguration
} from '@/types/crm-phone-agent';
import {
  DefaultApi as CrmScreenerApi,
  Configuration as CrmScreenerApiConfiguration
} from '@/types/crm-screener';
import {
  DefaultApi as CrmScreenerApiApi,
  Configuration as CrmScreenerApiApiConfiguration
} from '@/types/crm-screener-api';
import {
  DefaultApi as CrmScreenerInsurancesApi,
  Configuration as CrmScreenerInsurancesConfiguration
} from '@/types/crm-screener-insurances';
import {
  DefaultApi as CrmScreenerIntakeApi,
  Configuration as CrmScreenerIntakeConfiguration
} from '@/types/crm-screener-intake';
import {
  DefaultApi as CrmScreenerKno2Api,
  Configuration as CrmScreenerKno2Configuration
} from '@/types/crm-screener-kno2';
import {
  DefaultApi as DataCenterApi,
  Configuration as DataCenterConfiguration
} from '@/types/data-center';
import {
  DefaultApi as Incidentsv2Api,
  Configuration as Incidentsv2Configuration
} from '@/types/incidentsv2';
import {
  DefaultApi as MedicationsApi,
  Configuration as MedicationsConfiguration
} from '@/types/medications';
import {
  DefaultApi as NotificationsApi,
  Configuration as NotificationsConfiguration
} from '@/types/notifications';
import {
  Configuration as ResidentResourcesConfiguration,
  DefaultApi as ResidentResourcesIntegration
} from '@/types/resident-resources';
import {
  Configuration as ResidentConfiguration,
  DefaultApi as ResidentIntegration
} from '@/types/residents';
import {
  DefaultApi as UsersApi,
  Configuration as UsersConfiguration
} from '@/types/users';

import { createGlobalErrorHandler } from './globalErrorHandler';
import { newVersionMiddleware } from './newVersionMiddleware';

function setupConfigurationParams(basePath: string): ConfigurationParameters {
  return {
    basePath,
    middleware: [
      {
        pre: async (context) => {
          const accessToken = await window.getAccessTokenSilently({
            audience: import.meta.env.VITE_AUTH0_AUDIENCE
          });
          context.init.headers = {
            ...context.init.headers,
            Authorization: `Bearer ${accessToken}`,
            'x-fe-page': window.location.href
          };
          return context;
        },
        post: async (context) => {
          const globalHandler = createGlobalErrorHandler();
          const response = await globalHandler.post(context);

          return newVersionMiddleware(response);
        }
      }
    ]
  };
}

export const appClient = new AppApi(
  new AppConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_APP)
  )
);

export const dataCenterClient = new DataCenterApi(
  new DataCenterConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_DATA_CENTER)
  )
);

export const phoneAgentClient = new PhoneAgentApi(
  new PhoneAgentConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_PHONE_AGENT)
  )
);

export const incidentsv2Client = new Incidentsv2Api(
  new Incidentsv2Configuration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_INCIDENTS_V2)
  )
);

export const notificationsClient = new NotificationsApi(
  new NotificationsConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_NOTIFICATIONS)
  )
);

export const medicationsClient = new MedicationsApi(
  new MedicationsConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_MEDICATION)
  )
);

export const carePlanClient = new CarePlanApi(
  new CarePlanConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_CARE_PLAN)
  )
);

export const usersv2Client = new UsersApi(
  new UsersConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_USERS)
  )
);

export const CRMClient = new CRMApi(
  new CRMConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_CRM)
  )
);

export const residentsClient = new ResidentIntegration(
  new ResidentConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_RESIDENTS)
  )
);

export const residentsResourcesClient = new ResidentResourcesIntegration(
  new ResidentResourcesConfiguration(
    setupConfigurationParams(
      import.meta.env.VITE_API_BASE_PATH_RESIDENT_RESOURCES
    )
  )
);

export const crmCampaignsClient = new CrmCampaignsApi(
  new CrmCampaignsConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_CRM_CAMPAIGNS)
  )
);

export const crmScreenerClient = new CrmScreenerApi(
  new CrmScreenerApiConfiguration(
    setupConfigurationParams(import.meta.env.VITE_API_BASE_PATH_CRM_SCREENER)
  )
);

export const crmScreenerApiClient = new CrmScreenerApiApi(
  new CrmScreenerApiApiConfiguration(
    setupConfigurationParams(
      import.meta.env.VITE_API_BASE_PATH_CRM_SCREENER_API
    )
  )
);

export const crmScreenerInsurancesClient = new CrmScreenerInsurancesApi(
  new CrmScreenerInsurancesConfiguration(
    setupConfigurationParams(
      import.meta.env.VITE_API_BASE_PATH_CRM_SCREENER_INSURANCES
    )
  )
);

export const crmScreenerIntakeClient = new CrmScreenerIntakeApi(
  new CrmScreenerIntakeConfiguration(
    setupConfigurationParams(
      import.meta.env.VITE_API_BASE_PATH_CRM_SCREENER_INTAKE
    )
  )
);

export const crmScreenerKno2Client = new CrmScreenerKno2Api(
  new CrmScreenerKno2Configuration(
    setupConfigurationParams(
      import.meta.env.VITE_API_BASE_PATH_CRM_SCREENER_KNO2
    )
  )
);
